<template>
  <article
    class="crm-deal-card"
    :class="{
      'is-late': isLate,
      'is-no-action': hasNoNextAction,
      'is-won': oportunidade.status === 'ganha',
      'is-lost': oportunidade.status === 'perdida',
      'is-draggable': draggable,
      'is-dragging': dragging,
    }"
    :draggable="draggable"
    @dragend="$emit('drag-end', oportunidade)"
    @dragstart="onDragStart"
  >
    <button
      class="crm-deal-main"
      type="button"
      @click="$emit('open', oportunidade)"
    >
      <div class="crm-deal-top">
        <span
          class="crm-deal-temperature"
          :style="temperatureStyle"
          role="button"
          tabindex="0"
          title="Alterar temperatura"
          @click.stop.prevent="$emit('temperature', oportunidade)"
          @keydown.enter.stop.prevent="$emit('temperature', oportunidade)"
          @keydown.space.stop.prevent="$emit('temperature', oportunidade)"
        >
          <i :class="temperatureIcon"></i>
          {{ temperatureText }}
        </span>
      </div>
      <strong class="crm-deal-title">{{ oportunidade.titulo }}</strong>
      <span class="crm-deal-contact">{{ contactText }}</span>
      <div v-if="contactPhone" class="crm-deal-phone">
        <i class="la la-whatsapp"></i>
        <span>
          {{ contactPhone }}
        </span>
      </div>
      <div class="crm-deal-next-action">
        <i class="la la-calendar-check-o"></i>
        <span>{{ nextActionText }}</span>
      </div>
    </button>
  </article>
</template>

<script>
export default {
  name: "CrmDealCard",
  props: {
    oportunidade: {
      type: Object,
      required: true,
    },
    draggable: {
      type: Boolean,
      default: true,
    },
    dragging: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    temperatureOptions: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    contactText() {
      return (
        this.oportunidade.contato ||
        this.oportunidade.pessoa ||
        "Sem cliente ou contato"
      );
    },
    contactPhone() {
      return (
        this.oportunidade.contato_telefone ||
        this.oportunidade.telefone_contato ||
        this.oportunidade.pessoa_telefone ||
        ""
      );
    },
    isLate() {
      return (
        this.oportunidade.proxima_atividade_em &&
        new Date(this.oportunidade.proxima_atividade_em) < new Date()
      );
    },
    hasNoNextAction() {
      return !this.oportunidade.proxima_atividade_em;
    },
    nextActionText() {
      if (!this.oportunidade.proxima_atividade_em) {
        return "Sem próxima ação";
      }

      return new Date(this.oportunidade.proxima_atividade_em).toLocaleString(
        "pt-BR"
      );
    },
    temperatureOption() {
      return this.temperatureOptions.find(
        (option) => option.value === this.oportunidade.temperatura
      );
    },
    temperatureText() {
      if (this.temperatureOption && this.temperatureOption.text) {
        return this.temperatureOption.text;
      }

      const labels = {
        muito_frio: "Muito frio",
        frio: "Frio",
        morno: "Morno",
        quente: "Quente",
        venda_certa: "Venda certa",
      };

      return labels[this.oportunidade.temperatura] || "Sem temperatura";
    },
    temperatureIcon() {
      const icons = {
        muito_frio: "la la-thermometer-empty",
        frio: "la la-thermometer-quarter",
        morno: "la la-thermometer-half",
        quente: "la la-thermometer-three-quarters",
        venda_certa: "la la-thermometer-full",
      };

      return icons[this.oportunidade.temperatura] || "la la-thermometer-empty";
    },
    temperatureStyle() {
      const color =
        this.temperatureOption?.cor ||
        this.oportunidade.cor ||
        this.oportunidade.etapa_cor ||
        (this.isLate ? "#f64e60" : "#0bb7d4");

      return {
        borderColor: color,
        color,
      };
    },
  },
  methods: {
    onDragStart(event) {
      if (!this.draggable) {
        event.preventDefault();
        return;
      }

      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData(
          "text/plain",
          String(this.oportunidade.id_crm_oportunidade)
        );
      }

      this.$emit("drag-start", this.oportunidade);
    },
  },
};
</script>

<style scoped>
.crm-deal-card {
  background: #ffffff;
  border: 1px solid #dfe5ee;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(23, 31, 56, 0.04);
  margin-bottom: 10px;
  overflow: hidden;
  transition: border-color 0.16s ease, box-shadow 0.16s ease,
    transform 0.16s ease, opacity 0.16s ease;
}

.crm-deal-card.is-late {
  border-color: #ffd6de;
}

.crm-deal-card.is-won {
  border-color: #b9f1dd;
}

.crm-deal-card.is-lost {
  border-color: #ffd6de;
}

.crm-deal-card.is-draggable {
  cursor: grab;
}

.crm-deal-card.is-dragging {
  box-shadow: 0 10px 28px rgba(23, 31, 56, 0.16);
  opacity: 0.58;
  transform: scale(0.985);
}

.crm-deal-main {
  background: transparent;
  border: 0;
  color: #25324b;
  display: block;
  padding: 11px 12px 9px;
  text-align: left;
  width: 100%;
}

.crm-deal-main:hover {
  background: #fbfdff;
}

.crm-deal-top {
  align-items: center;
  color: #647084;
  display: flex;
  font-size: 12px;
  gap: 7px;
  margin-bottom: 8px;
}

.crm-deal-temperature {
  align-items: center;
  background: #ffffff;
  border: 1px solid currentColor;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  font-size: 11px;
  font-weight: 800;
  gap: 4px;
  line-height: 1;
  max-width: 128px;
  min-height: 22px;
  min-width: 0;
  overflow: hidden;
  padding: 4px 6px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.crm-deal-temperature:hover,
.crm-deal-temperature:focus {
  box-shadow: 0 0 0 3px rgba(11, 183, 212, 0.14);
  outline: none;
}

.crm-deal-temperature i {
  margin-left: 0;
}

.crm-deal-title {
  display: block;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.crm-deal-contact {
  color: #647084;
  display: block;
  font-size: 12px;
  margin-top: 4px;
  overflow-wrap: anywhere;
}

.crm-deal-phone {
  align-items: center;
  color: #1c7c54;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  gap: 5px;
  margin-top: 8px;
  max-width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
}

.crm-deal-next-action {
  align-items: center;
  background: #f3f6fb;
  border-radius: 6px;
  color: #647084;
  display: flex;
  font-size: 12px;
  gap: 6px;
  margin-top: 10px;
  min-height: 30px;
  padding: 6px 8px;
}

.crm-deal-next-action span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.is-late .crm-deal-next-action {
  background: #fff2f5;
  color: #d63952;
}

.is-no-action .crm-deal-next-action {
  background: #fff8dd;
  color: #946500;
}
</style>
